<template>
  <DialogComponent
    class="section-container"
    :okAction="save"
    :cancelAction="close"
    :showAction="initProduct"
    :isMandatory="true"
    :okActionName="isEdit ? 'Update' : 'Save'"
    :headerName="isEdit ? 'Edit product' : 'Create product'"
    :isValid="isValid"
    :validateAction="validationForm">
    <div slot="dialog-body" v-if="isShow">
      <div class="main-content">
        <div class="main-element">
          <TextComponent
            class="form-element form-group"
            fieldName="ID"
            :disabled="isEdit"
            @getValue="checkProductId"
            :isValid="isEdit ? isEdit : (isExist || validProductId || !!product.productId)"
            :isMandatory="true"
            :isExist="isExist"
            errorMessage="This ID is already in use. Please choose a different ID."
            v-model="product.productId" />
        </div>
        <div class="main-element">
          <TextComponent
            class="form-element form-group"
            fieldName="Name"
            @getValue="getValue"
            v-model="product.name"
            :isValid="isEdit ? isEdit : validName"
            :isMandatory="true" />
          <div class="mana-content">
            <button class="btn" @click="openPreview">Preview</button>
          </div>
        </div>
        
      </div>

      <div class="dialog-body">
        <TabComponent :tabItems="productGroupItems" @changeValue="setTabName" />
        <div v-if="tabGroupName === 'Details'" class="main-content form-container main-settings">
          <div class="main-element">
            <DropdownComponent
              :optionItems="getOptionValues('family')"
              :selectValue="product.family"
              :isMandatory="true"
              :isValid="validFamily"
              @changeValue="(value) => setPropertyValue(value, 'family')"
              fieldName="Family" />
            <DropdownComponent
              class="form-element"
              :optionItems="getAllProductIds()"
              :selectValue="getParentName(product.parentId)"
              :isClearField="true"
              :isSearch="true"
              @changeValue="(value) => setPropertyValue(value, 'parentId')"
              fieldName="Parent" />
          </div>
          <div class="main-element">
            <DropdownComponent
              :optionItems="getOptionValues('group')"
              :selectValue="product.group"
              :isMandatory="true"
              :isValid="validGroup"
              @changeValue="(value) => setPropertyValue(value, 'group')"
              fieldName="Group" />
            <DropdownComponent
              class="form-element"
              :optionItems="getClusterNames()"
              :selectValue="product.cluster"
              :isMandatory="true"
              :isValid="validCluster"
              @changeValue="(value) => setPropertyValue(value, 'cluster')"
              :isSearch="true"
              :isAddNewValue="true"
              helpText="To create a new Cluster, you need to enter a Cluster name in the field and save/update the product."
              fieldName="Cluster" />
            <DropdownComponent
              v-if="sectionName === 'All products' || isDuplicate"
              :optionItems="getOptionValues('sectionName')"
              :selectValue="product.sectionName"
              :isMandatory="true"
              :isValid="validSectionName"
              @changeValue="(value) => setPropertyValue(value, 'sectionName')"
              fieldName="Section" />
          </div>
        </div>
        <form
          v-else-if="tabGroupName === 'Benefits'"
          class="row form-body eds-form form-container">
          <div class="sm-12 md-12 lg-12 form-group">
            <TextareaComponent
              class="form-element"
              v-for="index in maxBenefitsCount"
              :key="'benefit_' + index"
              :label="'Benefits ' + index"
              v-model="product.benefits[index - 1]" />
          </div>
        </form>
        <form
          class="row form-body eds-form form-container specification-items"
          v-else-if="tabGroupName === 'Specifications'">
          <div class="specification-container">
            <ul type="none" @click="selectedElement">
              <li
                v-for="(item, index) in product.specifications"
                :key="index"
                draggable
                @dragstart="startDrag($event, index)"
                @drop="onDrop($event, index)"
                @dragover.prevent
                @dragenter.prevent>
                <ProductSpecificationComponent
                  @selectedValue="selectedElement"
                  :specification="item"
                  :index="index"
                  :selectedIndex="selectedIndex" />
              </li>
            </ul>
          </div>

          <div class="bottom">
            <button
              class="btn warning"
              :disabled="selectedIndex == -1"
              @click="removeSpecificationItem">
              Remove
            </button>
            <button class="btn" @click="addSpecification">Add</button>
          </div>
        </form>
        <div
          v-else-if="tabGroupName === 'Assets'"
          class="main-content form-container">
          <AssetsDropdownComponent class="main-element" :product="product" />
        </div>
      </div>
      
    </div>
  </DialogComponent>
</template>

<script>
import DialogComponent from '../base.component/DialogComponent.vue';
import TabComponent from '../base.component/TabComponent.vue';
import TextComponent from '../base.component/TextComponent.vue';
import CheckboxComponent from '../base.component/CheckboxComponent.vue';
import DropdownComponent from '../base.component/DropdownComponent.vue';
import ProductApiService from '../../services/productApiService';
import ProductService from '../../services/productService';
import {
  TAB_GROUP_NAMES,
  MAX_BENEFITS_COUNT,
  KALTURA
} from '../../constants/adminProduct';
import TextareaComponent from '../base.component/TextareaComponent.vue';
import ProductSpecificationComponent from './ProductSpecificationComponent.vue';
import AssetsDropdownComponent from './AssetsDropdownComponent.vue';
import ProductPreviewComponent from './ProductPreviewComponent.vue';

export default {
  components: {
    DialogComponent,
    TabComponent,
    TextComponent,
    CheckboxComponent,
    DropdownComponent,
    TextareaComponent,
    ProductSpecificationComponent,
    AssetsDropdownComponent,
    ProductPreviewComponent
  },

  data() {
    return {
      productGroupItems: [
        {
          name: TAB_GROUP_NAMES.DETAILS,
          isActive: true,
        },
        {
          name: TAB_GROUP_NAMES.BENEFITS,
          isActive: false,
        },
        {
          name: TAB_GROUP_NAMES.SPECIFICATIONS,
          isActive: false,
        },
        {
          name: TAB_GROUP_NAMES.ASSETS,
          isActive: false,
        },
      ],

      tabGroupName: TAB_GROUP_NAMES.DETAILS,

      product: {
        id: '',
        name: '',
        family: '',
        group: '',
        sectionName: '',
        productId: '',
        parentId: '',
        rotatingProductSettings: {
          alpha: '',
          beta: '',
        },
        benefits: [],
        specifications: [],
        colorCodeImagePath: '',
        dataSheetFilePath: '',
        landscapeImagePath: '',
        modelFileName: '',
        orthoPath: '',
        sectionImageFileName: '',
        slides: [],
        video: null,
        isMana: false,
        cluster: '',
        fileName: ''
      },

      isEdit: false,
      isDuplicate: false,
      maxBenefitsCount: MAX_BENEFITS_COUNT,
      selectedIndex: -1,
      isShow: false,
      isValid: true,
      validCluster: true,
      validSectionName: true,
      validFamily: true,
      validGroup: true,
      validName: true,
      validProductId: true,
      isExist: false
    };
  },

  props: {
    sectionName: {
      type: String,
      required: true,
    },
  },

  watch: {
    product: function() {
      this.getItems();

      if (!this.isEdit) {
        const spec = {
          name: '',
          value: '',
          arrayValue: [],
        };

        this.product.specifications.push(spec);
      }
    },

    'product.productId': function() {
      this.product.productId = this.product.productId.replace(/\s/g, '_');
    }
  },

  methods: {
    getItems() {
      this.productGroupItems.forEach(
        (item, index) => (item.isActive = index === 0)
      );
    },

    addBenefit(event) {
      event.preventDefault();
      const benefit = '';

      this.product.benefits.push(benefit);
    },

    addSpecification(event) {
      event.preventDefault();
      const spec = {
        name: '',
        value: '',
        arrayValue: [],
      };

      this.product.specifications.push(spec);
    },

    removeSpecificationItem(event) {
      event.preventDefault();
      this.product.specifications.splice(this.selectedIndex, 1);
      this.selectedIndex = -1;
    },

    getOptionValues(fieldName) {
      return ProductService.getOptionValues(fieldName);
    },

    async save() {
      if (this.product.parentId === '') {
        delete this.product.parentId;
      }

      if (this.isEdit) {
        if (
          this.product.video?.entryId != '' &&
          this.product.video?.entryId != null
        ) {
          this.product.video.wid = KALTURA.WID;
          this.product.video.uiconfId = KALTURA.UICONF_ID;
          this.product.video.targetId = `kaltura_player_${this.product.video.entryId}`;
        } else {
          if (!!this.product.video) {
            this.product.video.wid = '';
            this.product.video.uiconfId = -1;
            this.product.video.targetId = '';
          }
        }

        await ProductApiService.update(this.product);
      } else {
        if (!this.isDuplicate) {
          if (this.sectionName != 'All products') {
            this.product.sectionName = this.sectionName;
          }
          this.product.fileName = this.product.name.replace(new RegExp(/[\s\.]/gm), '_');
          this.product.rotatingProductSettings = {
            alpha: 1.6,
            beta: 1.5,
          };
        } else {
          this.product.fileName = this.product.productId + '_d';
          this.product.isPublished = false;
        }

        await ProductApiService.createProduct(this.product);
      }
      this.isEdit = false;
      this.isDuplicate = false;
      this.setTabName(TAB_GROUP_NAMES.DETAILS);
      this.$emit('updateDataTable');

      this.isShow = !this.isShow;
    },

    setTabName(name) {
      this.tabGroupName = name;
    },

    setPropertyValue(value, property) {
      if (property === 'parentId') {
        this.product.parentId = ProductService.getProduct(value).id;
      } else {
        this.product[property] = value.trim();
      }
    },

    getParentName(parentId) {
      const id = parentId?.trim();
      return parentId ? ProductService.getProductIdName(id) : '';
    },

    close() {
      this.isValid = true;
      this.validCluster = true;
      this.validSectionName = true;
      this.validFamily = true;
      this.validGroup = true;
      this.validName = true;
      this.validProductId = true;
      this.isExist = false;
      this.isEdit = false;
      this.isDuplicate = false;
      this.setTabName(TAB_GROUP_NAMES.DETAILS);
      this.isShow = !this.isShow;
    },

    initProduct() {
      this.isShow = !this.isShow;
      if (!this.isEdit && !this.isDuplicate) {
        this.product = this.clearProduct();
      }

      if (this.isDuplicate) {
        this.product.id = '';
        this.product.productId = '';
      }
    },

    getAllProductIds() {
      return ProductService.getProductsIdNames(this.sectionName);
    },

    getClusterNames() {
      return ProductService.getClusterNames(this.sectionName);
    },

    selectedElement(index) {
      if (typeof index == 'number') {
        this.selectedIndex = this.selectedIndex == index ? -1 : index;
      }
    },

    startDrag(event, index) {
      event.dataTransfer.dropEffect = 'move';
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('itemID', index);
    },

    onDrop(event, index) {
      const prevIndex = event.dataTransfer.getData('itemID');

      const move = (from, to, ...arr) =>
        from === to ? arr : (arr.splice(to, 0, ...arr.splice(from, 1)), arr);

      this.product.specifications = move(
        prevIndex,
        index,
        ...this.product.specifications
      );
    },

    getValue() {
      this.validName = !!this.product.name;
    },

    async checkProductId(value) {
      if (value != '') {
        const searchCriteria = {
          productIdName: value.trim(),
        };

        await ProductApiService.getProduct(searchCriteria).then(
        (data) => (this.isExist = data.length !== 0));
      } else {
        this.isExist = false;
      }
    },

    openPreview() {
      ProductService.setLocalProduct(this.product);
      const prevRef = this.$router.resolve({ path: '/preview' });

      window.open(prevRef.href);
    },

    clearProduct() {
      return {
        id: '',
        name: '',
        family: '',
        group: '',
        sectionName: '',
        productId: '',
        parentId: '',
        rotatingProductSettings: {
          alpha: '',
          beta: '',
        },
        benefits: [],
        specifications: [],
        colorCodeImagePath: '',
        dataSheetFilePath: '',
        landscapeImagePath: '',
        modelFileName: '',
        orthoPath: '',
        sectionImageFileName: '',
        slides: [],
        video: null,
        isMana: false,
        cluster: '',
        fileName: ''
      }
    },

    validationForm() {
      if (this.sectionName != 'All products' && !this.isDuplicate) {
          this.product.sectionName = this.sectionName;
        }

      if (
        !!this.product.name &&
        !!this.product.family &&
        !!this.product.group &&
        !!this.product.sectionName &&
        !!this.product.productId &&
        !!this.product.cluster
      ) {
        this.isValid = true;
      } else {
        this.isValid = false;
      }

      this.checkTextField();
      this.checkDropdownField();

      return this.isValid && !this.isExist;
    },

    checkTextField() {
      this.validName = !!this.product.name;
      this.validProductId = !!this.product.productId;
    },

    checkDropdownField() {
      this.validFamily = !!this.product.family;
      this.validGroup = !!this.product.group;
      this.validSectionName = !!this.product.sectionName;
      this.validCluster = !!this.product.cluster;
    }
  },
};
</script>

<style lang="less" scoped>
@import '~@eds/vanilla/variables/global.less';
@import '~@eds/vanilla/variables/dark.less';

.section-container {
  padding: @space-base @space-large;
}

.detail-view {
  margin-top: 15px;
  display: flex;
}

.form-container {
  overflow: scroll;
  max-height: 40vh;
  min-height: 10vh;
  overflow-x: hidden;
}

@media screen and (min-width: 1440px) {
  .form-container {
    max-height: 50vh;
  }
}

@media screen and (min-width: 1920px) {
  .form-container {
    max-height: 60vh;
  }
}

.dialog-body {
  width: 90vw;
  height: 85vh;
  padding-top: 1rem;
}

.mr-1 {
  margin-right: 1rem;
}

.specification-container {
  border: solid 1px @gray-46;
  padding: 1rem;
  margin-bottom: 1rem;
}

.specification-items {
  display: block;
}

.main-content {
  display: flex;
}

.main-element {
  width: -webkit-fill-available;
}

.mana-content {
  float: right;
  padding-top: 1rem;
  padding-right: 0.5rem;
}

.mandatory-text {
  position: absolute;
  top: 56rem
}

.main-settings {
    height: 90vh;
    overflow: scroll;
  }
</style>